import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../../context/API';
import { 
  CustomerData, 
  CustomersWithOrdersResponse,
  FetchCustomerSalesParams,
  FetchCustomersWithOrdersParams 
} from '../types';

interface CustomersState {
  customerData: CustomerData | null;
  customerOrderData: CustomersWithOrdersResponse | null;
  loading: boolean;
  orderLoading: boolean;
  error: string | null;
}

const initialState: CustomersState = {
  customerData: null,
  customerOrderData: null,
  loading: false,
  orderLoading: false,
  error: null,
};

export const fetchCustomerSales = createAsyncThunk<
  CustomerData,
  FetchCustomerSalesParams,
  { rejectValue: string }
>(
  'customers/fetchCustomerSales',
  async (params, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get<{ success: boolean; data: CustomerData }>(
        '/analytics/customer-sales',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch customer sales data');
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch customer sales data'
      );
    }
  }
);

export const fetchCustomersWithOrders = createAsyncThunk<
  CustomersWithOrdersResponse,
  FetchCustomersWithOrdersParams,
  { rejectValue: string }
>(
  'customers/fetchCustomersWithOrders',
  async (params, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get<{ success: boolean; data: CustomersWithOrdersResponse }>(
        '/analytics/customers-with-orders',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch customers with orders');
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch customers with orders'
      );
    }
  }
);

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCustomerData: (state) => {
      state.customerData = null;
    },
    clearCustomerOrderData: (state) => {
      state.customerOrderData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customer sales
      .addCase(fetchCustomerSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerSales.fulfilled, (state, action) => {
        state.loading = false;
        state.customerData = action.payload;
      })
      .addCase(fetchCustomerSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch customers with orders
      .addCase(fetchCustomersWithOrders.pending, (state) => {
        state.orderLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomersWithOrders.fulfilled, (state, action) => {
        state.orderLoading = false;
        state.customerOrderData = action.payload;
      })
      .addCase(fetchCustomersWithOrders.rejected, (state, action) => {
        state.orderLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCustomerData, clearCustomerOrderData } = customersSlice.actions;
export default customersSlice.reducer;
